'use client';

import Script from 'next/script';
import { useEffect, useState } from 'react';
import { useAuthStore } from '@/stores/authStore';

declare global {
  interface Window {
    HubSpotConversations: {
      widget: {
        load: () => void;
        remove: () => void;
        open: () => void;
        close: () => void;
      };
    };
    hsConversationsSettings: {
      loadImmediately: boolean;
      [key: string]: unknown;
    };
  }
}

const getHubSpotPortalId = (): string => {
  const portalId = process.env.NEXT_PUBLIC_HUBSPOT_PORTAL_ID || '';

  if (!portalId) {
    console.warn(
      'HubSpot Portal ID not configured. Please set NEXT_PUBLIC_HUBSPOT_PORTAL_ID environment variable.'
    );
    return '';
  }
  return portalId;
};

export const HUBSPOT_SHOW_CHAT_BUTTON_PATHS = ['/company-verification', '/add-employee'];

export default function HubSpotChat() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const portalId = getHubSpotPortalId();
  const [shouldLoad, setShouldLoad] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || !portalId) return;

    // Load HubSpot after 3 seconds to avoid Lighthouse penalties
    const timer = setTimeout(() => {
      setShouldLoad(true);
    }, 3000);

    const toggleHandler: EventListener = () => {
      // If HubSpot hasn't been loaded yet, load it first
      if (!window.HubSpotConversations?.widget) {
        setShouldLoad(true);
        return;
      }

      // Check if the chat widget is currently visible
      const hubspotContainer = document.querySelector(
        '#hubspot-messages-iframe-container'
      ) as HTMLElement;

      if (hubspotContainer) {
        // Look for chat interface elements that indicate the chat is open
        const chatIframe = hubspotContainer.querySelector('iframe');
        const chatExpanded = hubspotContainer.querySelector(
          '[data-test-id="chat-widget-expanded"]'
        );

        // Check if the chat is actually open by looking for the expanded interface
        // or if the iframe is visible and has content
        let isCurrentlyVisible = false;

        if (chatExpanded) {
          isCurrentlyVisible = (chatExpanded as HTMLElement).style.display !== 'none';
        } else if (chatIframe) {
          // Check if the iframe is visible and has content
          const iframeElement = chatIframe as HTMLIFrameElement;
          isCurrentlyVisible =
            iframeElement.style.display !== 'none' &&
            iframeElement.style.visibility !== 'hidden' &&
            iframeElement.offsetHeight > 100; // Chat interface is typically larger than launcher
        }

        if (isCurrentlyVisible) {
          window.HubSpotConversations.widget.close();
        } else {
          window.HubSpotConversations.widget.open();
        }
      } else {
        window.HubSpotConversations.widget.open();
      }
    };

    window.addEventListener('toggle-hubspot-chat', toggleHandler);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('toggle-hubspot-chat', toggleHandler);
    };
  }, [isAuthenticated, portalId]);

  if (!isAuthenticated || !portalId || !shouldLoad) {
    return null;
  }

  return (
    <Script
      src={`https://js.hs-scripts.com/${portalId}.js`}
      strategy="lazyOnload"
      onLoad={() => {
        window.HubSpotConversations?.widget?.load();
      }}
    />
  );
}
